import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/context/AuthContext";
import "../../../styles/features/auth/loginModal.css";
import "../../../styles/features/auth/login.css";
import LoginBoxContent from "../components/LoginBoxContent";
import ModalHeader from "../../../shared/components/layout/ModalHeader";
import { consumePostLoginRedirect } from "../utils/postLoginRedirect";
import { readLastLoginUsername } from "../../../utils/lastLoginIdentity";

const LoginModal = () => {
  const navigate = useNavigate();
  const {
    showLoginModal,
    setShowLoginModal,
    login,
    loginError,
    isTokenExpired,
    loginModalVariant,
    setLoginModalVariant,
  } = useAuth();
  const [formData, setFormData] = useState({
    username: readLastLoginUsername(),
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  // Prefill username when the session-expired modal opens (password stays empty).
  useEffect(() => {
    if (!showLoginModal || loginModalVariant === "full-login") return;
    setFormData((prev) => ({
      ...prev,
      username: prev.username || readLastLoginUsername(),
    }));
  }, [showLoginModal, loginModalVariant]);

  if (!showLoginModal) {
    return null;
  }

  if (loginModalVariant === "full-login") {
    return (
      <div className="modal-overlay login-modal-overlay">
        <div className="modal-container login-modal-container">
          <ModalHeader
            variant="details"
            title="Login"
            onClose={() => {
              setShowLoginModal(false);
              setLoginModalVariant("session-expired");
            }}
          />
          <div className="modal-content login-modal-content">
          <LoginBoxContent
            className="login-box login-box-modal-variant login-box-modal-body"
            onLoginSuccess={() => {
              setShowLoginModal(false);
              setLoginModalVariant("session-expired");
              const redirected = consumePostLoginRedirect(navigate);
              if (!redirected) {
                // Stay on the current public page after optional sign-in.
              }
            }}
            showLogo={false}
            showTitle={false}
          />
          </div>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const signedIn = await login(formData.username, formData.password);
      // Session-expired sign-in stays on the open page. Reload so leftover
      // expired-login state does not block the next save or add.
      if (signedIn) {
        window.location.reload();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay login-modal-overlay">
      <div className="modal-container login-modal-container session-expired-modal-container">
        <ModalHeader
          variant="details"
          title="Session Expired"
          onClose={() => {
            setShowLoginModal(false);
            setLoginModalVariant("session-expired");
          }}
        />
        <div className="modal-content login-modal-body">
          <p>
            {isTokenExpired
              ? "Your session has expired. Please sign in again."
              : "Please sign in."}
          </p>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username" className="field-label-stacked">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password" className="field-label-stacked">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
              />
            </div>
            {loginError && <div className="error-message">{loginError}</div>}
            <div className="form-actions">
              <button
                type="submit"
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? "Signing In..." : "Sign In"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginModal; 